import { useContext, useMemo } from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type { QuickSummaryErrorResponseMessage } from '@atlassian/convo-ai-api/convo-api/generated';
import type { StreamError as AssistantServiceStreamError } from '@atlassian/ai-agent-streaming/stream-message';
import { ConvoAIErrorMessage } from '@atlassian/ai-agent-streaming/convo-ai-streaming-models';
import type { StreamError as ConvoAIStreamError } from '@atlassian/ai-agent-streaming/convo-ai-stream-messages';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

export const tooMuchContentString =
	'Atlassian Intelligence can’t generate a summary for you right now, as there is too much content to summarize.';
export const notEnoughContentString =
	'Atlassian Intelligence can’t generate a summary for you right now, as there is not enough content to summarize. You can try again later if more is added!';

export const enum HandledErrorType {
	TooMuchContent = 'tooMuchContent',
	NotEnoughContent = 'notEnoughContent',
	RateLimit = 'rateLimit',
	AUPViolation = 'aupViolation',
	GeneralError = 'generalError',
}

const ERROR_MAP: { [key: string]: HandledErrorType } = {
	[tooMuchContentString]: HandledErrorType.TooMuchContent,
	[notEnoughContentString]: HandledErrorType.NotEnoughContent,
	TOO_MANY_REQUEST: HandledErrorType.RateLimit,
	AUP_VIOLATION: HandledErrorType.AUPViolation,
};

const STREAM_ERROR_MAP: { [key: string]: HandledErrorType } = {
	[ConvoAIErrorMessage.ANSWER_UNAVAILABLE_FOR_CONTENT]: HandledErrorType.NotEnoughContent,
	[ConvoAIErrorMessage.NOT_ENOUGH_CONTENT_ERROR]: HandledErrorType.NotEnoughContent,
	[ConvoAIErrorMessage.RATE_LIMIT]: HandledErrorType.RateLimit,
	[ConvoAIErrorMessage.ACCEPTABLE_USE_VIOLATIONS]: HandledErrorType.AUPViolation,
};

const expectedErrorTypes: Array<HandledErrorType> = [
	HandledErrorType.TooMuchContent,
	HandledErrorType.NotEnoughContent,
	HandledErrorType.AUPViolation,
];

type SummaryErrorHelperData = {
	isExpectedError: boolean;
	stopExperienceTracking: () => void;
	errorType?: HandledErrorType;
	error: Error;
};

export const useSummaryErrorHelper = (
	experienceName: string,
	errorFromApolloOrStream?:
		| ApolloErrorInterop
		| AssistantServiceStreamError
		| ConvoAIStreamError<QuickSummaryErrorResponseMessage['message_template'] | string>,
): SummaryErrorHelperData => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { errorType, isExpectedError, error } = useMemo(() => {
		if (!errorFromApolloOrStream)
			return {
				isExpectedError: false,
				stopExperienceTracking: () => {},
				errorType: undefined,
				error: new Error('[useSummaryErrorHelper]: error instance is missing'),
			};

		let errorMessageFromGQLorStream = '';
		let errorTypeFromGQLorStream = '' as HandledErrorType;

		if ('graphQLErrors' in errorFromApolloOrStream) {
			// The BE only returns one error (the first that it encounters).
			// Therefore, we don't need to loop through the array, and can just rely on index 0
			errorMessageFromGQLorStream = errorFromApolloOrStream.graphQLErrors[0].message;
			errorTypeFromGQLorStream = ERROR_MAP[errorMessageFromGQLorStream];
			return {
				isExpectedError: expectedErrorTypes.includes(errorTypeFromGQLorStream),
				error: errorFromApolloOrStream,
				errorType: errorTypeFromGQLorStream,
			};
		} else {
			errorMessageFromGQLorStream = errorFromApolloOrStream.message.message_template;
			errorTypeFromGQLorStream = STREAM_ERROR_MAP[errorMessageFromGQLorStream];
			return {
				isExpectedError: expectedErrorTypes.includes(errorTypeFromGQLorStream),
				error: new Error(errorFromApolloOrStream.message.content),
				errorType: errorTypeFromGQLorStream,
			};
		}
	}, [errorFromApolloOrStream]);

	const stopExperienceTracking = () => {
		if (isExpectedError) {
			markErrorAsHandled(error);
			experienceTracker.abort({
				name: experienceName,
				reason: `Can not generate summary due to ${errorType}`,
			});
		} else {
			experienceTracker.fail({
				name: experienceName,
				error,
			});
		}
	};

	return {
		isExpectedError,
		stopExperienceTracking,
		error,
		errorType,
	};
};
